import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, Eye } from 'lucide-react';
import { fireEvent } from '@/lib/analytics';
import FavButton from './FavButton';

// localStorage keys used by this component:
// - ns_recent_domains: array of viewed domain objects
// - ns_recent_hide: "true" to hide the section entirely

interface RecentDomain {
  domain: string;
  viewedAt: number;
  bin_price?: number;
  tld?: string;
  bundle?: string;
}

interface RecentlyViewedProps {
  currentDomain: string;
}

export default function RecentlyViewed({ currentDomain }: RecentlyViewedProps) {
  const [recentDomains, setRecentDomains] = useState<RecentDomain[]>([]);
  const [isHidden, setIsHidden] = useState(false);

  // Load recent domains and hide state from localStorage
  useEffect(() => {
    const loadRecentDomains = () => {
      try {
        const storedDomains = localStorage.getItem('ns_recent_domains');
        const storedHideState = localStorage.getItem('ns_recent_hide');

        if (storedDomains) {
          const parsed = JSON.parse(storedDomains);
          if (Array.isArray(parsed)) {
            setRecentDomains(parsed);
          }
        } else {
          setRecentDomains([]);
        }

        setIsHidden(storedHideState === 'true');
      } catch (error) {
        console.debug('Error loading recent domains from localStorage:', error);
        setRecentDomains([]);
        setIsHidden(false);
      }
    };

    // Load initially
    loadRecentDomains();

    // Listen for storage changes (from other tabs/windows)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'ns_recent_domains' || e.key === 'ns_recent_hide') {
        loadRecentDomains();
      }
    };

    // Listen for custom events (from same tab)
    const handleCustomStorageChange = () => {
      loadRecentDomains();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('recently-viewed-updated', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('recently-viewed-updated', handleCustomStorageChange);
    };
  }, []);

  // Filter and prepare domains for display
  const displayDomains = useMemo(() => {
    return recentDomains
      .filter((domain) => domain.domain !== currentDomain) // Exclude current domain
      .sort((a, b) => b.viewedAt - a.viewedAt) // Most recent first
      .slice(0, 10); // Limit to 10 items
  }, [recentDomains, currentDomain]);

  // Track analytics when section shows
  useEffect(() => {
    if (displayDomains.length > 0 && !isHidden) {
      fireEvent('recently_viewed_show', { count: displayDomains.length });
    }
  }, [displayDomains.length, isHidden]);

  // Handle clear functionality
  const handleClear = () => {
    try {
      localStorage.removeItem('ns_recent_domains');
      setRecentDomains([]);
      fireEvent('recently_viewed_clear');
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('recently-viewed-updated'));
    } catch (error) {
      console.debug('Error clearing recent domains:', error);
    }
  };

  // Handle hide functionality
  const handleHide = () => {
    try {
      localStorage.setItem('ns_recent_hide', 'true');
      setIsHidden(true);
      fireEvent('recently_viewed_hide');
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('recently-viewed-updated'));
    } catch (error) {
      console.debug('Error hiding recent domains:', error);
    }
  };

  // Don't render if hidden or no domains to show
  if (isHidden || displayDomains.length === 0) {
    return null;
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <section className="mt-12" aria-labelledby="recently-viewed-heading">
      <div className="mb-8">
        <div className="flex items-center justify-center mb-4">
          <h2
            id="recently-viewed-heading"
            className="text-3xl font-bold text-foreground flex items-center gap-2"
          >
            <Eye className="w-8 h-8 text-primary" />
            Recently viewed
          </h2>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Clear recently viewed domains"
          >
            Clear
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleHide}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Hide recently viewed section"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {displayDomains.map((domain) => (
          <li key={domain.domain}>
            <Link to={`/domain/${domain.domain}`} className="block">
              <Card className="apple-card h-full hover:shadow-lg transition-shadow duration-200 cursor-pointer relative">
                {/* Favorite Button */}
                <div className="absolute top-3 right-3 z-10">
                  <FavButton domain={domain.domain} size="sm" variant="ghost" />
                </div>

                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex-grow">
                    <h3 className="text-lg font-bold text-foreground hover:text-primary transition-colors block mb-2">
                      {domain.domain}
                    </h3>

                    {domain.bin_price && (
                      <p className="text-sm text-muted-foreground mb-4">
                        {formatPrice(domain.bin_price)}
                      </p>
                    )}
                  </div>

                  <div className="mt-auto inline-flex items-center text-sm font-medium text-primary">
                    View Details →
                  </div>
                </CardContent>
              </Card>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
